import {
  type GetContentHeroBannerQuery,
  type GetPlacementsQuery,
  GetPlacementsDocument,
  type GetPlacementsQueryVariables,
  PlacementTypeEnum,
  ButtonVariantEnum,
  PlacementStatusEnum,
  type PlacementPlatformEnum,
  PlacementDestinationTypeEnum,
} from 'domains/merchandising/api'
import { useMeshQuery } from 'legacy/apollo/clientHooks'

export type DashboardQueryResult = GetContentHeroBannerQuery['storefrontPlacement']
type DashboardDestination = DashboardQueryResult['destination']
type MeshQueryResult = GetPlacementsQuery['contentManagementServiceGetPlacements']
type MeshPlacement = MeshQueryResult['placements'][0]

interface HeroBannerMeshArgs {
  placementId: string
  retailerId: string
}

export const parseDestination = (p: MeshPlacement): DashboardDestination => {
  let type: PlacementDestinationTypeEnum = null
  let target: string = null
  let displayValue: string = null
  if (p.banner.defaultBanner?.ctaAction) {
    if (p.banner.defaultBanner?.ctaAction.navigateToUrl) {
      type = PlacementDestinationTypeEnum.External
      target = p.banner.defaultBanner.ctaAction.navigateToUrl.url
    } else if (p.banner.defaultBanner?.ctaAction.navigateToDepartment) {
      type = PlacementDestinationTypeEnum.Department
      target = p.banner.defaultBanner.ctaAction.navigateToDepartment.departmentId
    } else if (p.banner.defaultBanner?.ctaAction.navigateToCollection) {
      type = PlacementDestinationTypeEnum.Collection
      target = p.banner.defaultBanner.ctaAction.navigateToCollection.slug
    } else if (p.banner.defaultBanner?.ctaAction.navigateToProduct) {
      type = PlacementDestinationTypeEnum.Product
      target = p.banner.defaultBanner.ctaAction.navigateToProduct.productId
    }
  } else if (p.banner.defaultBanner.action && p.banner.defaultBanner.actionData) {
    // legacy support, type does not need to conform to enum
    type = p.banner.defaultBanner.action as any
    target = p.banner.defaultBanner.actionData
  }

  if (
    type !== PlacementDestinationTypeEnum.Department &&
    type !== PlacementDestinationTypeEnum.Collection &&
    type !== PlacementDestinationTypeEnum.Product
  ) {
    displayValue = target
  }

  return {
    type,
    target,
    displayValue,
  }
}

const mapResult = (data: MeshQueryResult): DashboardQueryResult => {
  if (!data) {
    return
  }

  const p = data.placements[0]

  return {
    id: p.id,
    bannerId: p.banner.defaultBanner?.id,
    name: p.internalName,
    // cast to enum for typing
    platform: p.platform.map(e => e && (String(e) as PlacementPlatformEnum)),
    status: (p.status && (String(p.status) as PlacementStatusEnum)) || PlacementStatusEnum.Active,
    type:
      (p.banner.defaultBanner?.variant &&
        (String(p.banner.defaultBanner?.variant) as PlacementTypeEnum)) ||
      PlacementTypeEnum.Image,
    backgroundColor: p.banner.defaultBanner?.backgroundColorHex,
    imageAltText: p.banner.defaultBanner?.imageAltText,
    images: {
      large: p.banner.defaultBanner?.imageUrl,
      small: p.banner.defaultBanner?.imageMobileUrl,
    },
    destination: parseDestination(p),
    scheduling: {
      startDate: p.startDate,
      endDate: p.endDate,
      timezone: p.timezone,
    },
    headingText: {
      text: p.banner.defaultBanner?.title,
      color: p.banner.defaultBanner?.titleColorHex,
    },
    subheadingText: {
      text: p.banner.defaultBanner?.subTitle,
      color: p.banner.defaultBanner?.subTitleColorHex,
    },
    descriptionText: {
      text: p.banner.defaultBanner?.tagline,
      color: p.banner.defaultBanner?.taglineColorHex,
    },
    button: {
      label: p.banner.defaultBanner?.cta,
      styleVariant: ButtonVariantEnum.Primary,
    },
    banners: [],
  }
}

export const useGetQueryHeroBannerMesh = ({ placementId, retailerId }: HeroBannerMeshArgs) => {
  const variables = {
    placementId,
    retailerId,
  }

  const { data, error, loading } = useMeshQuery<GetPlacementsQuery, GetPlacementsQueryVariables>(
    GetPlacementsDocument,
    {
      fetchPolicy: 'cache-and-network',
      variables,
    }
  )

  return {
    apiResult: mapResult(data?.contentManagementServiceGetPlacements),
    apiSuccess: Boolean(data),
    apiLoading: loading,
    apiError: error,
  }
}
